import { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Typography, Divider, IconButton, Tooltip,
  Select, MenuItem, Chip, LinearProgress, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import EditIcon        from "@mui/icons-material/Edit";
import StopIcon        from "@mui/icons-material/Stop";
import UndoIcon        from "@mui/icons-material/Undo";
import RedoIcon        from "@mui/icons-material/Redo";
import DeleteIcon      from "@mui/icons-material/Delete";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import PanToolAltIcon  from "@mui/icons-material/PanToolAlt";
import AddRoadIcon     from "@mui/icons-material/AddRoad";
import SaveIcon        from "@mui/icons-material/Save";
import WpDeleteIcon    from "@mui/icons-material/Delete";

import ToolDialog           from "../ToolDialog";
import { useSnackbar } from '../../../context/SnackbarContext';

const bulf_feature_update_url = 'https://app.mapgeoid.com/api-service/feature_service/bulk_feature_edit';
import RouteEditorCore, { WP_TYPES, TYPE_COLORS } from "./RouteEditorCore";
import FeatureAttributeForm from "../GeometryEditor/ArtributeForm";

// ─────────────────────────────────────────────────────────
const TYPE_LABELS = {
  [WP_TYPES.START]:   "Start",
  [WP_TYPES.STOP]:    "Stop",
  [WP_TYPES.THROUGH]: "Through",
  [WP_TYPES.BREAK]:   "Break",
  [WP_TYPES.VIA]:     "Via",
};
const EDITABLE_TYPES = [WP_TYPES.THROUGH, WP_TYPES.BREAK, WP_TYPES.VIA];

// ─────────────────────────────────────────────────────────
// WAYPOINT LIST
// ─────────────────────────────────────────────────────────
const WaypointList = ({ waypoints, canDelete, onTypeChange, onDelete }) => {
  if (!waypoints.length) return (
    <Typography variant="caption" color="text.disabled"
      sx={{ display: "block", textAlign: "center", py: 2 }}>
      No waypoints — click map to add
    </Typography>
  );

  return (
    <List dense disablePadding>
      {waypoints.map((wp, i) => {
        const isFirst    = i === 0;
        const isLast     = i === waypoints.length - 1;
        const locked     = isFirst || isLast;
        const [lon, lat] = wp.lonLat;
        return (
          <ListItem key={wp.id} dense divider sx={{
            py: 0.4, px: 1,
            background: isFirst ? "rgba(46,125,50,0.05)"
                      : isLast  ? "rgba(198,40,40,0.05)"
                      : "transparent",
          }}>
            <Box sx={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0, mr: 1,
              background: TYPE_COLORS[wp.type] ?? "#333",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>
                {wp.index + 1}
              </Typography>
            </Box>

            <ListItemText
              primary={
                <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: "text.secondary" }}>
                  {lat.toFixed(5)}, {lon.toFixed(5)}
                </Typography>
              }
              secondary={
                locked ? (
                  <Chip label={TYPE_LABELS[wp.type]} size="small" sx={{
                    height: 14, fontSize: 9, fontWeight: 700, mt: 0.25,
                    background: TYPE_COLORS[wp.type], color: "#fff",
                  }} />
                ) : (
                  <Select value={wp.type} onChange={e => onTypeChange(wp.index, e.target.value)}
                    size="small" variant="standard"
                    MenuProps={{ style: { zIndex: 2500 } }}
                    sx={{ fontSize: 11, mt: 0.25, minWidth: 80 }}>
                    {EDITABLE_TYPES.map(t => (
                      <MenuItem key={t} value={t} sx={{ fontSize: 11 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: TYPE_COLORS[t], flexShrink: 0 }} />
                          {TYPE_LABELS[t]}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                )
              }
              sx={{ my: 0 }}
            />

            <ListItemSecondaryAction>
              {!locked && canDelete && (
                <IconButton size="small" onClick={() => onDelete(wp.index)} sx={{ p: 0.25 }}>
                  <WpDeleteIcon sx={{ fontSize: 13, color: "error.main" }} />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        );
      })}
    </List>
  );
};

// ─────────────────────────────────────────────────────────
// ROUTE EDITOR DIALOG
// ─────────────────────────────────────────────────────────
export default function RouteEditorDialog({ title, map, layers, onClose }) {
  const editorRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  const [status, setStatus] = useState({
    editing: false, loading: false, mode: "none",
    undo: 0, redo: 0, waypoints: 0,
    canAddFeature: false, editingFeatureId: null,
  });
  const [waypoints,       setWaypoints]       = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState("");
  const [pendingFeature,  setPendingFeature]  = useState(null);
  const [commitData,      setCommitData]      = useState(null);
  const [stopGuard,       setStopGuard]       = useState(false);

  const selectedLayer = layers[selectedLayerId] ?? null;

  // ── Init editor on layer change ──
  useEffect(() => {
    if (!map || !selectedLayer) return;
    editorRef.current = new RouteEditorCore(
      map,
      selectedLayer.vectorLayer,
      (msg, lvl) => console.warn(`[RouteEditor] ${msg}`, lvl ?? ""),
      {
        onStatusChange:    s   => setStatus({ ...s }),
        onWaypointsChange: wps => setWaypoints(wps),
        onFeatureAdded:    f   => setPendingFeature(f),
        onCommit:          d   => setCommitData(d),
      }
    );
    return () => { editorRef.current?.destroy(); editorRef.current = null; };
  }, [selectedLayerId]);

  // ── Toggle edit ──
  const handleToggleEdit = useCallback(() => {
    if (!status.editing) {
      editorRef.current?.startEdit();
    } else {
      if (status.undo > 0) {
        setStopGuard(true);
      } else {
        editorRef.current?.stopEdit();
      }
    }
  }, [status.editing, status.undo]);

  // ── Stop guard ──
  const handleGuardReviewAndCommit = useCallback(() => {
    setStopGuard(false);
    editorRef.current?.commit();
  }, []);

  const handleGuardDiscardAndStop = useCallback(() => {
    setStopGuard(false);
    editorRef.current?.rollbackAll();
    editorRef.current?.stopEdit();
  }, []);

  // ── Commit ──
  const handleCommit         = useCallback(() => editorRef.current?.commit(), []);
  const handleCommitConfirm = useCallback(async () => {
    if (!commitData) return;
    const { commits, payload } = commitData;
    try {
      const res = await fetch(bulf_feature_update_url, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      editorRef.current?.confirmCommit();
      setCommitData(null);
      editorRef.current?.stopEdit();
      showSnackbar("Changes committed successfully", "success");
    } catch (err) {
      showSnackbar(`Commit failed: ${err.message}`, "error");
    }
  }, [commitData, showSnackbar]);

  const handleCommitRollback = useCallback(() => {
    editorRef.current?.rollbackAll();
    setCommitData(null);
    editorRef.current?.stopEdit();
    showSnackbar("All changes rolled back", "info");
  }, [showSnackbar]);
  const handleCommitCancel   = useCallback(() => setCommitData(null), []);

  // ── Attr form ──
  const handleAttrSubmit = useCallback((attrMap) => {
    if (pendingFeature) Object.entries(attrMap).forEach(([k, v]) => pendingFeature.set(k, v));
    setPendingFeature(null);
  }, [pendingFeature]);

  const handleAttrCancel = useCallback(() => {
    if (pendingFeature) selectedLayer?.vectorLayer.getSource().removeFeature(pendingFeature);
    setPendingFeature(null);
  }, [pendingFeature, selectedLayer]);

  const modeLabel = !status.editing ? "Idle"
    : status.mode === "draw"   ? "Drawing"
    : status.mode === "select" ? (status.editingFeatureId ? "Modifying" : "Select route")
    : "Idle";

  const modeColor = !status.editing ? "default"
    : status.mode === "draw"   ? "warning"
    : status.mode === "select" && status.editingFeatureId ? "success"
    : "default";

  const schema = selectedLayer?.property_schema ?? [];

  return (
    <>
      <ToolDialog title={title} open={true} onClose={onClose}>
        <Box>

          {/* Loading */}
          {status.loading && <LinearProgress sx={{ height: 2 }} />}

          {/* Layer selector */}
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">Route Layer</Typography>
            <Select fullWidth size="small" value={selectedLayerId}
              onChange={e => setSelectedLayerId(e.target.value)}
              disabled={status.editing} displayEmpty
              MenuProps={{ style: { zIndex: 2000 } }}
              sx={{ mt: 0.5 }}
              renderValue={v => v
                ? (layers[v]?.name ?? v)
                : <em style={{ color: "rgba(0,0,0,0.4)" }}>Select route layer</em>
              }>
              {Object.entries(layers).map(([id, l]) => (
                l.is_route && <MenuItem key={id} value={id}>{l.name}</MenuItem>
              ))}
            </Select>
            {status.editing && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                Stop editing to change layer
              </Typography>
            )}
          </Box>

          <Divider />

          {/* ── Toolbar — mirrors GeometryEditor ── */}
          <Box sx={{ display: "flex", flexWrap: "wrap", px: 1, py: 0.5, gap: 0.5, justifyContent: "center" }}>

            {/* Start / Stop */}
            <Tooltip title={status.editing ? "Stop Edit" : "Start Edit"}>
              <span>
                <IconButton onClick={handleToggleEdit} disabled={!selectedLayerId}
                  color={status.editing ? "error" : "default"} size="small">
                  {status.editing ? <StopIcon /> : <EditIcon />}
                </IconButton>
              </span>
            </Tooltip>

            {/* Select / Modify */}
            <Tooltip title="Select / Modify Route">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.enableSelectMode()}
                  disabled={!status.editing}
                  color={status.editing && status.mode === "select" ? "primary" : "default"}
                  size="small">
                  <PanToolAltIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Draw Route */}
            <Tooltip title="Draw Route">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.enableDrawMode()}
                  disabled={!status.editing}
                  color={status.editing && status.mode === "draw" ? "primary" : "default"}
                  size="small">
                  <AddLocationAltIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Delete */}
            <Tooltip title="Delete Selected Route">
              <span>
                <IconButton
                  onClick={() => editorRef.current?.deleteSelected()}
                  disabled={!status.editing || status.mode !== "select" || !status.editingFeatureId}
                  size="small">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Undo */}
            <Tooltip title={`Undo (${status.undo})`}>
              <span>
                <IconButton size="small" onClick={() => editorRef.current?.undo()} disabled={status.undo === 0}>
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Redo */}
            <Tooltip title={`Redo (${status.redo})`}>
              <span>
                <IconButton size="small" onClick={() => editorRef.current?.redo()} disabled={status.redo === 0}>
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Commit / Save */}
            <Tooltip title="Commit Changes">
              <span>
                <IconButton size="small" color="primary" onClick={handleCommit}
                  disabled={!status.editing || status.undo === 0}>
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Divider />

          {/* Status bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, flexWrap: "wrap", background: "rgba(0,0,0,0.02)" }}>
            <Chip label={modeLabel} size="small" color={modeColor} sx={{ fontSize: 10, height: 20 }} />
            <Typography variant="caption" color="text.secondary">
              Points: <strong>{status.waypoints}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Undo: <strong>{status.undo}</strong>
            </Typography>
            {status.undo > 0 && (
              <Typography variant="caption" color="warning.main" fontWeight={500}>Unsaved</Typography>
            )}
            {status.loading && (
              <Typography variant="caption" color="primary.main" fontWeight={500}>Routing…</Typography>
            )}
          </Box>

          {/* Hint */}
          {status.editing && (
            <Box sx={{ px: 1.5, py: 0.4, background: "rgba(25,118,210,0.03)" }}>
              <Typography variant="caption" color="text.disabled">
                {status.mode === "draw"
                  ? "Click map to add waypoints · Backspace removes last"
                  : status.mode === "select" && status.editingFeatureId
                  ? "Drag markers or line to reroute"
                  : status.mode === "select"
                  ? "Click an existing route to edit it"
                  : "Choose Draw or Select mode"}
              </Typography>
            </Box>
          )}

          <Divider />

          {/* Waypoint list */}
          <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
            <WaypointList
              waypoints={waypoints}
              canDelete={status.editing && waypoints.length > 2}
              onTypeChange={(idx, type) => editorRef.current?.setGuidePointType(idx, type)}
              onDelete={(idx) => editorRef.current?.removeGuidePoint(idx)}
            />
          </Box>

          {/* Add Feature button — draw mode with valid route */}
          {status.canAddFeature && (
            <>
              <Divider />
              <Box sx={{ px: 1.5, py: 1 }}>
                <Button fullWidth variant="contained" size="small"
                  startIcon={<AddRoadIcon />}
                  onClick={() => editorRef.current?.addFeature({})}
                  sx={{ textTransform: "none", fontSize: 12 }}>
                  Add Route Feature
                </Button>
              </Box>
            </>
          )}

        </Box>
      </ToolDialog>

      {/* ── Stop guard ── */}
      <Dialog open={stopGuard} onClose={() => setStopGuard(false)} maxWidth="xs" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have <strong>{status.undo}</strong> unsaved change{status.undo !== 1 ? "s" : ""}. What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopGuard(false)} size="small">Stay in Edit</Button>
          <Button onClick={handleGuardDiscardAndStop} color="error" variant="outlined" size="small">Discard & Stop</Button>
          <Button onClick={handleGuardReviewAndCommit} color="primary" variant="contained" size="small">Review & Commit</Button>
        </DialogActions>
      </Dialog>

      {/* ── Attribute form ── */}
      <Dialog open={!!pendingFeature} onClose={handleAttrCancel}
        maxWidth="xs" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>Route Attributes</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {schema.length > 0 ? (
            <FeatureAttributeForm
              feature={pendingFeature}
              schema={schema}
              onSubmit={handleAttrSubmit}
              onCancel={handleAttrCancel}
            />
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No attribute schema for this layer.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="small" variant="contained" onClick={() => setPendingFeature(null)}>OK</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Commit dialog ── */}
      <Dialog open={!!commitData} onClose={handleCommitCancel} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
        <DialogTitle>
          Commit Changes
          <Typography variant="caption" color="text.secondary" display="block">
            {commitData?.commits?.size ?? 0} feature{(commitData?.commits?.size ?? 0) !== 1 ? "s" : ""} affected
          </Typography>
        </DialogTitle>
        <DialogContent>
          {commitData && commitData.commits?.size > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feature ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(commitData.commits.entries()).map(([id, commit]) => (
                  <TableRow key={id}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{String(id)}</TableCell>
                    <TableCell>
                      <Chip label={commit.type === "NEW" ? "New" : "Existing"} size="small"
                        variant="outlined" color={commit.type === "NEW" ? "primary" : "default"}
                        sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      {commit.state === "add"    && <Chip label="Added"    size="small" color="success" sx={{ fontSize: 10 }} />}
                      {commit.state === "delete" && <Chip label="Deleted"  size="small" color="error"   sx={{ fontSize: 10 }} />}
                      {commit.state === "update" && <Chip label="Modified" size="small" color="warning" sx={{ fontSize: 10 }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No changes to commit.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommitCancel} size="small">Cancel</Button>
          <Button onClick={handleCommitRollback} color="error" variant="outlined" size="small">Rollback All</Button>
          <Button onClick={handleCommitConfirm} color="primary" variant="contained" size="small"
            disabled={!commitData || commitData.commits?.size === 0}>
            Commit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}